"use client";

import { ChevronDownIcon, Pencil1Icon, PlusCircledIcon } from "@radix-ui/react-icons";
import { Button, DropdownMenu, Flex, Text } from "@radix-ui/themes";
import { UseQueryResult } from "@tanstack/react-query";
import Image from "next/image";
import { useSession } from "next-auth/react";
import { useEffect, useState } from "react";
import PhoneInput from "react-phone-input-2";
import { AccountInfo, AccountsResponse } from "@/app/api/v1/accounts/utils";
import { AuthProvider } from "@/auth-types";
import { TelephoneNumberInfo } from "@/clients/attnow/types";
import RequestUbidDialog from "@/components/RequestUbidDialog";
import useTNStore from "@/stores/TNStore";
import useUBStore from "@/stores/UBStore";
import useVFCCSessionStore, { VerificationState } from "@/stores/VFCCSessionStore";
import { formattedTN, TN_REQUIRED_LENGTH } from "@/utils/general";
import { CustomError } from "@/utils/network";

interface TNSelectProps {
    tn?: string;
    onSelect: (tn: TelephoneNumberInfo) => void;
    tns: TelephoneNumberInfo[];
}

function TNSelect({ tn, onSelect, tns }: TNSelectProps) {
    return (
        <DropdownMenu.Root>
            <DropdownMenu.Trigger>
                <Button
                    style={{
                        borderRadius: "48px",
                        borderWidth: "thin",
                        borderColor: "black",
                        borderStyle: "solid",
                        height: "48px",
                        width: "294px",
                        fontSize: "16px",
                        fontWeight: 400,
                    }}
                    color="gray"
                    variant="surface"
                    className="min-w-full h-12 p-3 rounded-lg justify-between">
                    {tn ? formattedTN(tn) : "Select a phone number"}
                    <ChevronDownIcon width={24} height={24} />
                </Button>
            </DropdownMenu.Trigger>
            <DropdownMenu.Content>
                {tns.map((tnInfo) => (
                    <DropdownMenu.Item className="justify-start gap-2 p-2 h-10" key={tnInfo.tn} onSelect={() => onSelect(tnInfo)}>
                        {formattedTN(tnInfo.tn)}
                    </DropdownMenu.Item>
                ))}
            </DropdownMenu.Content>
        </DropdownMenu.Root>
    );
}

function SessionState({
    accountsQueryResult,
    tns,
}: {
    accountsQueryResult: UseQueryResult<AccountsResponse, CustomError> | undefined;
    tns: TelephoneNumberInfo[] | undefined;
}) {
    // this should only be called if HaloB is selected and there is no response from accountsquery
    const { vfccSession, setVFCCSession } = useVFCCSessionStore();

    useEffect(() => {
        if (accountsQueryResult?.data === undefined && !(accountsQueryResult?.isFetching || accountsQueryResult?.isLoading)) {
            setVFCCSession({
                ...vfccSession,
                state: VerificationState.ERROR,
                message: "Telephone Number not found in account",
            });
        }
    }, [accountsQueryResult?.data, tns]);
    if (accountsQueryResult?.data !== undefined && (accountsQueryResult.isFetching || accountsQueryResult.isLoading)) {
        return (
            <Text as="div" size="2" mb="1" weight="bold">
                Loading phone numbers...
            </Text>
        );
    }
    return (
        <Text as="div" size="2" mb="1" weight="bold">
            No phone numbers found on account. Please try signing in again or return to Business Center
        </Text>
    );
}
interface HaloProps {
    tn: string;
    setTN: (tn: string) => void;
    accountsQueryResult?: UseQueryResult<AccountsResponse, CustomError>;
    accountQueryResult?: UseQueryResult<AccountInfo, CustomError>;
}

function HaloB({ tn, setTN, accountsQueryResult }: HaloProps) {
    const { vfccSession } = useVFCCSessionStore();
    const tns = vfccSession.tnInfoList || accountsQueryResult?.data?.accounts.flatMap((account) => account.tns);
    if (!tns) {
        return <SessionState accountsQueryResult={accountsQueryResult} tns={tns}></SessionState>;
    }
    return <TNSelect tn={tn} onSelect={(tnInfo) => setTN(tnInfo.tn)} tns={tns} />;
}

function HaloE({ tn, setTN, accountQueryResult }: HaloProps) {
    const { ubid } = useUBStore();
    const { vfccSession } = useVFCCSessionStore();
    const tns = vfccSession.tnInfoList || accountQueryResult?.data?.tns;
    const [forceInput, setForceInput] = useState<boolean>(false);
    const [ubidDialogOpen, setUbidDialogOpen] = useState<boolean>(false);

    const handleTNChange = (phoneNumber: string) => {
        setTN(phoneNumber);
    };

    return (
        <>
            <RequestUbidDialog open={ubidDialogOpen} setOpen={setUbidDialogOpen} />
            <Flex justify="start">
                <Button variant="ghost" onClick={() => setUbidDialogOpen(true)}>
                    {!ubid ? <PlusCircledIcon /> : <Pencil1Icon />}
                    {!ubid ? "Add account (Required for verification)" : `Change account (${ubid})`}
                </Button>
            </Flex>
            <label className="min-w-full">
                <Text as="div" size="2" mb="1" weight="bold">
                    Telephone Number
                </Text>
                {forceInput || !tns?.length ? (
                    <Flex direction="column" gap="1" align="stretch">
                        <PhoneInput
                            country={"us"}
                            onlyCountries={["us"]}
                            value={tn}
                            onChange={handleTNChange}
                            data-testid="my-tn"
                            inputProps={{ "data-testid": "my-tn" }}
                            placeholder={"+_ (___) ___-____"}
                            containerClass="min-w-full"
                            specialLabel=""
                            inputClass="!h-12 min-w-full rounded-lg disabled:cursor-wait"
                            disabled={!forceInput && (accountQueryResult?.isFetching || accountQueryResult?.isLoading)}
                        />
                        {!!tns?.length && (
                            <Flex justify="end">
                                <Button variant="ghost" onClick={() => setForceInput(false)}>
                                    View phone numbers on account
                                </Button>
                            </Flex>
                        )}
                    </Flex>
                ) : (
                    <Flex direction="column" gap="1" align="end">
                        <TNSelect tn={tn} onSelect={(tnInfo) => setTN(tnInfo.tn)} tns={tns} />
                        <Button variant="ghost" onClick={() => setForceInput(true)}>
                            Enter a different phone number
                        </Button>
                    </Flex>
                )}
            </label>
        </>
    );
}

export interface RequestTNDialogProps {
    open?: boolean;
    setOpen?: (open: boolean) => void;
}

function RequestTNComponent({
    accountsQueryResult,
    accountQueryResult,
}: {
    accountsQueryResult: UseQueryResult<AccountsResponse, CustomError>;
    accountQueryResult: UseQueryResult<AccountInfo, CustomError>;
}) {
    const { data: authSession } = useSession();

    const { tn, setTN } = useTNStore();
    const [txtTN, setTxtTN] = useState<string>(tn);

    useEffect(() => {
        setTxtTN(tn);
    }, [tn]);

    const handleNext = () => {
        setTN(txtTN);
    };

    const setTxtTNandHandleNext = (tn: string) => {
        setTxtTN(tn);
        setTN(tn);
    };

    return (
        <>
            <Flex gap="5" direction="column" align="stretch">
                <Flex justify="center" align="center">
                    <Image src="/noPhone.png" alt="AT&T Config Portal" width={140} height={140} className="mr-2" priority={true} />
                </Flex>

                <Flex justify="center" align="center">
                    <Text style={{ fontWeight: "500", fontSize: "26px" }}>No telephone number selected</Text>
                </Flex>

                {authSession?.user.provider === AuthProvider.HaloB && (
                    <HaloB tn={txtTN} setTN={setTxtTNandHandleNext} accountsQueryResult={accountsQueryResult} />
                )}
                {authSession?.user.provider === AuthProvider.HaloE && <HaloE tn={txtTN} setTN={setTxtTN} accountQueryResult={accountQueryResult} />}
                {!authSession && (
                    <Text as="div" size="2" mb="1" weight="bold">
                        Please sign in to change the phone number
                    </Text>
                )}
                {authSession?.user.provider === AuthProvider.HaloE && (
                    <Flex gap="3" justify="end" align="center">
                        {!!authSession && (
                            <Button
                                variant="surface"
                                onClick={handleNext}
                                disabled={txtTN.length !== TN_REQUIRED_LENGTH}
                                className="px-6 py-3 h-10 rounded-full cursor-pointer disabled:text-gray-400 disabled:cursor-default">
                                Update
                            </Button>
                        )}
                    </Flex>
                )}
            </Flex>
        </>
    );
}

export default RequestTNComponent;
